/**
 * Resumable 3D model upload.
 *
 * When a model is uploaded, the backend product entry is created first, then the file is pushed
 * to S3, then a processing job is created. If the session is interrupted between those steps
 * (reload / network drop / closed tab) we are left with a half-finished entry. This module
 * persists every in-flight NORMAL model upload to IndexedDB and provides the state machine to
 * resume it into the SAME product entry.
 *
 * See docs/resumable-3d-model-upload.md for the full design.
 *
 * IMPORTANT (performance): the file is kept as a Blob/File end-to-end. We never read it into an
 * ArrayBuffer on the main thread, and IndexedDB stores the Blob by reference — so persistence and
 * upload stay off the main thread even for very large (up to 1024 MB) files.
 */

import {
  STORE,
  addUploadedFilesIDB,
  getAllUploadedFilesIDB,
  getUploadedFileIDB,
  deleteUploadedFile,
} from './idb';
import {
  apiGetPropDataUserFilePresigned,
  apiPost3DModelJob,
  cancellableApiPostImageOnPresignedData,
} from './api';
import { propsReadApi } from '../api/curator';
import { API_UPLOAD_TYPE, STATUS_NOT_DONE_KEY } from '../constants/constants';
import { sendModelViewerExceptionToSentry, hasEnoughStorageSpace } from './jsHelper';

export const RESUME_PHASE = {
  S3_PENDING: 's3_pending', // entry created, file not yet uploaded to S3
  JOB_PENDING: 'job_pending', // file uploaded, job not yet created
};

/**
 * What a resume attempt settled on. The progress card needs this: a transient failure must keep
 * showing "Retrying…", while a permanent one has to become a real failure — returning a bare `null`
 * for both (as this used to) leaves a dead upload spinning forever.
 */
export const RESUME_OUTCOME = {
  DONE: 'done', // finished — the job exists; record deleted
  GONE: 'gone', // unrecoverable (entry deleted server-side / no blob); record discarded
  RETRY: 'retry', // transient — record kept, a later trigger tries again
};

// Fired after a record is persisted, so the resume hook can arm its safety-net poll right away.
export const RESUMABLE_UPLOAD_ADDED_EVENT = 'resumable-upload-added';

/* ------------------------------------------------------------------ heartbeat ------------ */

/**
 * "Someone is uploading this right now" marker.
 *
 * The resume poll ticks every 10s, including while a normal upload is still in flight. From IDB
 * alone a live upload and a dead one look identical (phase `s3_pending`, no job yet), so without
 * this marker resume would start a SECOND upload of the same file about 10s into every real
 * upload — and models go up to 1 GB.
 *
 * The beat is driven by the upload's own progress events, not by a timer. That matters: there is
 * no interval left running that could keep a record marked "alive" forever and block it from ever
 * being resumed. If the tab dies or the transfer stalls, the beats simply stop and the record is
 * free again after ACTIVE_UPLOAD_TTL_MS. Storing it in IDB (not memory) also covers other tabs.
 */
const HEARTBEAT_WRITE_MS = 3000; // never write the beat to IDB more often than this
const ACTIVE_UPLOAD_TTL_MS = 30000; // a beat older than this means the uploader is gone

const lastBeatWrittenAt = new Map(); // productId -> epoch ms of our last write (throttle only)

/**
 * Refresh the "still uploading" marker for a record. Safe to call on every progress event —
 * the IDB write is throttled to HEARTBEAT_WRITE_MS.
 */
export async function markUploadAlive(productId) {
  const id = String(productId ?? '');
  if (!id) return;
  const now = Date.now();
  if (now - (lastBeatWrittenAt.get(id) ?? 0) < HEARTBEAT_WRITE_MS) return;
  lastBeatWrittenAt.set(id, now);
  try {
    const record = await getUploadedFileIDB(id);
    if (record) await addUploadedFilesIDB({ ...record, heartbeatAt: now });
  } catch (e) {
    // Best-effort: a missed beat can only cause an early resume, never data loss.
  }
}

/** Is a live uploader (this tab or another) still working on this record? */
function isUploadActive(record) {
  const beat = record?.heartbeatAt;
  return beat != null && Date.now() - beat < ACTIVE_UPLOAD_TTL_MS;
}

function forgetBeat(productId) {
  lastBeatWrittenAt.delete(String(productId));
}

/** Drop the claim so resume can take the record over immediately (after an aborted transfer). */
export async function clearUploadBeat(productId) {
  const id = String(productId ?? '');
  if (!id) return;
  forgetBeat(id);
  try {
    const record = await getUploadedFileIDB(id);
    if (record) await addUploadedFilesIDB({ ...record, heartbeatAt: null });
  } catch (e) {
    // Best-effort: if this fails the record just waits out ACTIVE_UPLOAD_TTL_MS instead.
  }
}

/* ------------------------------------------------------------------ stall watch ---------- */

/**
 * Aborts a transfer that has stopped moving bytes.
 *
 * Losing the network does not reliably fail an in-flight request — the browser can hold the socket
 * open long after the bytes stopped (TCP retransmits, no OS-level notification). That zombie
 * request would keep a slot busy while resume eventually starts a second upload of the same file.
 *
 * IMPORTANT: STALL_ABORT_MS must stay BELOW ACTIVE_UPLOAD_TTL_MS. The abort clears the record's
 * claim so resume can take over cleanly; if the claim expired first, resume would start its
 * duplicate while this request was still open — exactly what the heartbeat exists to prevent.
 *
 * `navigator.onLine === false` is only used to give up sooner. We never treat `true` as "the
 * network works" — it isn't (captive portals, wifi-without-internet), and that case is precisely
 * the one the plain stall timeout catches.
 */
const STALL_ABORT_MS = 20000; // no progress for this long -> the transfer is dead
const STALL_ABORT_OFFLINE_MS = 10000; // ...and we know why -> give up sooner
const STALL_CHECK_MS = 2000;

export function createStallWatcher(onStall) {
  let lastProgressAt = Date.now();
  let bodySent = false;

  const timer = setInterval(() => {
    // Once the last byte is handed off we're waiting on S3's response, not stalling — aborting
    // here would throw away a transfer that already finished.
    if (bodySent) return;
    const limit = navigator?.onLine === false ? STALL_ABORT_OFFLINE_MS : STALL_ABORT_MS;
    if (Date.now() - lastProgressAt < limit) return;
    clearInterval(timer);
    onStall();
  }, STALL_CHECK_MS);

  return {
    progress: (event) => {
      lastProgressAt = Date.now();
      if (event?.total && event.loaded >= event.total) bodySent = true;
    },
    stop: () => clearInterval(timer),
  };
}

/* ------------------------------------------------------------------ persistence ---------- */

/**
 * Persist a freshly-created upload so it can be resumed. Call right after the product entry is
 * created and BEFORE the S3 upload starts.
 */
export async function persistPendingUpload({ productId, file, productData, meta = {} }) {
  if (!productId || !file) return false;
  // Don't persist if there isn't room — the live upload still proceeds, it just won't be resumable.
  if (!(await hasEnoughStorageSpace(file?.size))) {
    console.warn('persistPendingUpload skipped: not enough storage space (upload not resumable).');
    return false;
  }
  try {
    await addUploadedFilesIDB({
      id: String(productId),
      tempId: meta.tempId || null,
      phase: RESUME_PHASE.S3_PENDING,
      file, // Blob/File — stored by reference, not copied into memory
      name: meta.name || null,
      filename: meta.filename || file?.name || null,
      extension: meta.extension || null,
      size: file?.size || null,
      uploadType: meta.uploadType ?? API_UPLOAD_TYPE.NORMAL,
      status: meta.status ?? STATUS_NOT_DONE_KEY,
      productData: productData || null,
      orgId: meta.orgId ?? null,
      userId: meta.userId ?? null,
      createdAt: meta.now ?? null, // epoch ms; pass from caller (Date.now() is fine in app code)
      // The upload starts right after this, so the record is "alive" from now — otherwise the
      // first poll tick could fire before the first progress event and duplicate the upload.
      heartbeatAt: meta.now ?? Date.now(),
    });
    // Let the resume hook know there is now work to watch (arms its safety-net poll).
    window.dispatchEvent(new Event(RESUMABLE_UPLOAD_ADDED_EVENT));
    return true;
  } catch (e) {
    // Persistence is best-effort: if IDB is unavailable/over quota the live upload still proceeds,
    // it just won't be resumable. Never break the upload because of this.
    console.warn('persistPendingUpload failed (upload continues, not resumable):', e);
    return false;
  }
}

/** Mark the S3 upload as done; the only remaining step is the job-send. */
export async function markFileUploaded(productId) {
  const record = await getUploadedFileIDB(String(productId));
  if (!record) return;
  // Refresh the beat too — progress events have stopped by now, and this keeps the record claimed
  // across the job-send that follows.
  await addUploadedFilesIDB({
    ...record,
    phase: RESUME_PHASE.JOB_PENDING,
    heartbeatAt: Date.now(),
  });
}

/** Remove the persisted record — call ONLY after the file is uploaded AND the job-send succeeded. */
export async function completeUpload(productId) {
  forgetBeat(productId);
  await deleteUploadedFile(STORE.UPLOAD_FILES, String(productId));
}

/** Remove a persisted record without completing (explicit cancel / unrecoverable). */
export async function discardPendingUpload(productId) {
  forgetBeat(productId);
  await deleteUploadedFile(STORE.UPLOAD_FILES, String(productId));
}

/* ------------------------------------------------------------------ queries -------------- */

/** Records for the current user + org (scoped so we never resume someone else's uploads). */
export async function getResumableUploads({ orgId, userId } = {}) {
  let all = [];
  try {
    all = (await getAllUploadedFilesIDB()) || [];
  } catch (e) {
    console.warn('getResumableUploads: could not read IDB', e);
    return [];
  }
  const sameScope = (recVal, want) => want == null || recVal == null || String(recVal) === String(want);
  return all.filter(
    (r) => r?.id && sameScope(r.orgId, orgId) && sameScope(r.userId, userId)
  );
}

/**
 * Look up the current backend state for many product ids in as few calls as possible
 * (propsReadApi reads up to 20 ids per call). Returns a Map of id -> one of:
 *   { status: 'exists', product }  — the entry exists
 *   { status: 'gone' }             — the server answered and the entry isn't there
 *   { status: 'unknown' }          — the call failed (offline/error); do NOT act on it
 */
async function fetchProductStates(ids) {
  const states = new Map();
  const CHUNK = 20;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    let res;
    try {
      res = await propsReadApi(chunk, 1, null, { silent: true, noCache: true });
    } catch (_) {
      res = undefined;
    }
    const results = res?.results ?? res?.data?.results;
    if (!Array.isArray(results)) {
      // Couldn't determine (network error / unexpected shape) -> unknown; keep records.
      chunk.forEach((id) => states.set(String(id), { status: 'unknown' }));
      continue;
    }
    const found = new Map(
      results.filter((p) => p?.id != null).map((p) => [String(p.id), p])
    );
    chunk.forEach((id) => {
      const product = found.get(String(id));
      states.set(String(id), product ? { status: 'exists', product } : { status: 'gone' });
    });
  }
  return states;
}

/* ------------------------------------------------------------------ resume --------------- */

/**
 * Resume every interrupted upload for the current user + org.
 *
 * One batched read gives us each entry's backend state; then per record:
 *   - entry gone        -> delete the record
 *   - state unknown     -> leave the record (retry next time)
 *   - job already exists -> delete the record (idempotent: nothing left to do)
 *   - phase JOB_PENDING -> just send the job   (file already uploaded — no presigned/upload)
 *   - phase S3_PENDING  -> upload, then send the job
 * A record is deleted ONLY after its job-send actually succeeds.
 *
 * @returns {Promise<{products: object[], results: object[]}>}
 *   `products` — fresh product objects for uploads that finished, so the caller can patch those
 *   rows into the list in place (their product_job_status changed from null to a pending job once
 *   we sent it).
 *   `results` — one `{ productId, tempId, outcome }` per record attempted, so the caller can also
 *   report back to the upload progress card (see RESUME_OUTCOME).
 */
const RESUME_PACK_SIZE = 5; // how many uploads to run concurrently

export async function resumeAllPendingUploads({ orgId, userId } = {}) {
  const all = await getResumableUploads({ orgId, userId });
  // Leave anything a live uploader is still working on — resuming it would duplicate the transfer.
  const records = all.filter((record) => !isUploadActive(record));
  if (!records.length) return { products: [], results: [] };

  const states = await fetchProductStates(records.map((r) => r.id));
  const results = [];

  // Process in packs of 5: up to 5 records upload concurrently, and each one sends its own job
  // right after its file is uploaded.
  for (let i = 0; i < records.length; i += RESUME_PACK_SIZE) {
    const pack = records.slice(i, i + RESUME_PACK_SIZE);
    const packResults = await Promise.all(
      pack.map((record) => resumeOneRecord(record, states).catch(() => retryResult(record)))
    );
    results.push(...packResults);
  }

  const finished = results
    .filter((result) => result.outcome === RESUME_OUTCOME.DONE)
    .map((result) => result.productId);
  // Re-read the finished entries so the caller can refresh the list silently (in place) — their
  // status has changed since `states` was read at the start of this run.
  const products = finished.length ? await fetchProductsByIds(finished) : [];
  return { products, results };
}

/**
 * Resume a single record on demand — the manual "retry" button in the progress card.
 *
 * Deliberately the SAME path as the background run, so the button inherits its guards: it skips the
 * upload when the job already exists, and always fetches a fresh presigned URL (the one cached on
 * the row was minted before the failure and has very likely expired). Returns null when there is no
 * record to resume, so the caller can fall back to its own retry.
 */
export async function resumeOneUploadNow(productId) {
  const record = await getUploadedFileIDB(String(productId ?? ''));
  if (!record) return null;
  // A background run may already own this record — let it finish rather than racing it.
  if (isUploadActive(record)) return retryResult(record);
  const states = await fetchProductStates([record.id]);
  return resumeOneRecord(record, states).catch(() => retryResult(record));
}

const retryResult = (record) => ({
  productId: record?.id ?? null,
  tempId: record?.tempId ?? null,
  outcome: RESUME_OUTCOME.RETRY,
});

/**
 * Read the latest product objects for the given ids (chunked; propsReadApi reads up to 20/call).
 * Returns only the products the server actually returned — ids it doesn't know about are dropped.
 */
export async function fetchProductsByIds(ids = []) {
  const products = [];
  const CHUNK = 20;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    let res;
    try {
      res = await propsReadApi(chunk, 1, null, { silent: true, noCache: true });
    } catch (_) {
      res = undefined;
    }
    const results = res?.results ?? res?.data?.results;
    if (Array.isArray(results)) {
      products.push(...results.filter((p) => p?.id != null));
    }
  }
  return products;
}

/**
 * Resume a single record (upload if needed, then send its job). Always returns a
 * `{ productId, tempId, outcome }` result — never a bare null, because the caller has to tell a
 * transient failure ("keep showing Retrying…") from a permanent one ("show Failed").
 * Designed to run concurrently with other records.
 */
async function resumeOneRecord(record, states) {
  const settled = (outcome) => ({
    productId: record.id,
    tempId: record.tempId ?? null,
    outcome,
  });

  const state = states.get(String(record.id));
  if (!state || state.status === 'unknown') return settled(RESUME_OUTCOME.RETRY); // couldn't verify
  if (state.status === 'gone') {
    await discardPendingUpload(record.id); // entry deleted server-side
    return settled(RESUME_OUTCOME.GONE);
  }
  // A job already exists -> nothing left to do.
  if (state.product?.product_job_status != null) {
    await completeUpload(record.id);
    return settled(RESUME_OUTCOME.DONE);
  }

  // Re-read right before committing to work: the batched state read above takes a round trip, and
  // another tab may have claimed this record in the meantime. Then claim it ourselves so the next
  // poll tick sees it as busy.
  const current = await getUploadedFileIDB(String(record.id));
  if (!current || isUploadActive(current)) return settled(RESUME_OUTCOME.RETRY);
  await markUploadAlive(record.id);

  // Work off `current`, not the listing snapshot — its phase may have advanced since we read it.
  // JOB_PENDING: file is already in S3, just send the job. S3_PENDING: upload first.
  if (current.phase !== RESUME_PHASE.JOB_PENDING) {
    if (!current.file) {
      await discardPendingUpload(current.id); // no blob to upload -> unrecoverable
      return settled(RESUME_OUTCOME.GONE);
    }
    // upload failed -> keep, retry later
    if (!(await uploadModelFile(current))) return settled(RESUME_OUTCOME.RETRY);
    await markFileUploaded(current.id);
  }

  if (await sendModelJob(current)) {
    await completeUpload(current.id);
    return settled(RESUME_OUTCOME.DONE);
  }
  // job-send failed -> keep record (now job_pending) and retry on the next trigger
  return settled(RESUME_OUTCOME.RETRY);
}

async function uploadModelFile(record) {
  // Fetch a fresh presigned URL for the existing entry, then upload.
  const res = await apiGetPropDataUserFilePresigned(record.id, { silent: true, noCache: true });
  const presigned = res?.presigned_url_prop_user_file;
  if (!presigned) return false;

  // Resume's own transfers can stall the same way a live one can — abort rather than hold a zombie
  // request open. Returning false keeps the record so a later trigger retries it.
  const controller = new AbortController();
  const watcher = createStallWatcher(() => controller.abort());
  try {
    // Keep the record claimed for the whole transfer, so neither the next poll tick nor another
    // tab starts a duplicate upload of the same file.
    const uploaded = await cancellableApiPostImageOnPresignedData(
      record.file,
      presigned,
      (event) => {
        watcher.progress(event);
        markUploadAlive(record.id);
      },
      controller.signal
    );
    // An abort RESOLVES with no response instead of throwing, so a real response is required.
    // Treating an abort as success would flip the record to job_pending — and then send a job —
    // for a file that never landed in S3.
    return !!uploaded;
  } catch (err) {
    sendModelViewerExceptionToSentry({
      message: `Resume: model "${record?.name}" upload to S3 failed.`,
      file: record,
    });
    return false;
  } finally {
    watcher.stop();
  }
}

async function sendModelJob(record) {
  const formData = new FormData();
  formData.append('product', record.id);
  formData.append('status', record.status ?? STATUS_NOT_DONE_KEY);
  // api() RESOLVES with `undefined` on a network error (it doesn't reject), so a real response is
  // required to treat the job as actually sent.
  const res = await apiPost3DModelJob(formData, { silent: true }).catch(() => null);
  return !!res;
}
